const BASE_URL = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
let vapidPublicKey: string | null = null;

async function getVapidKey(): Promise<string> {
    if (vapidPublicKey) return vapidPublicKey;
    try {
        const response = await fetch(`${BASE_URL}/api/notifications/vapidPublicKey`);
        if (!response.ok) throw new Error('Failed to fetch VAPID key');
        const key = await response.text();
        vapidPublicKey = key;
        return key;
    } catch (error) {
        console.error('Error fetching VAPID public key:', error);
        throw new Error('Could not contact notification server.');
    }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

/**
 * Handles the browser-side process of asking for permission and getting a subscription object.
 * It does NOT send the subscription to the backend.
 */
export async function subscribeUser(): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported by your browser.');
  }

  const registration = await navigator.serviceWorker.ready;
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    return existingSubscription;
  }
  
  if (Notification.permission === 'denied') {
    throw new Error('Permission for notifications was denied. Please enable it in your browser settings.');
  }
  
  try {
    const applicationServerKey = urlBase64ToUint8Array(await getVapidKey());
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (error) {
    console.error('Failed to subscribe the user: ', error);
    if (error instanceof Error && error.name === 'NotAllowedError') {
        throw new Error('You have blocked notifications. To enable them, please go to your browser settings.');
    }
    throw new Error('Failed to subscribe for notifications due to a technical issue.');
  }
}

/**
 * Sends a push subscription object to the backend to be saved.
 */
// FIX: Removed the 'token' parameter and switched to using `credentials: 'include'` for cookie-based authentication.
export async function sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    await fetch(`${BASE_URL}/api/notifications/subscribe`, {
        method: 'POST',
        body: JSON.stringify(subscription),
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });
}

/**
 * Removes a push subscription from the backend and unsubscribes from the browser.
 */
export async function unsubscribeUser(): Promise<void> {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    // Tell backend to remove it
    await fetch(`${BASE_URL}/api/notifications/unsubscribe`, {
        method: 'POST',
        body: JSON.stringify({ endpoint: subscription.endpoint }),
        headers: { 'Content-Type': 'application/json' }
    });
    // Unsubscribe from browser
    await subscription.unsubscribe();
    console.log('User is unsubscribed.');
  }
}